/* eslint-disable react-hooks/exhaustive-deps */
import {
  FC,
  createContext,
  useContext,
  useLayoutEffect,
  useState,
  ReactNode,
} from 'react';

export interface PageLink {
  id?: string;
  title: string;
  fontIcon?: string;
  path: string;
  isActive: boolean;
  isSeparator?: boolean;
  /** Outros caminhos em que a aba segue marcada (a página aberta de dentro dela mora em outra rota). */
  alsoActiveOn?: string[];
}

export interface PageDataContextModel {
  pageTitle?: string;
  setPageTitle: (_title: string) => void;
  pageActions?: ReactNode;
  setPageActions: (_actions: ReactNode) => void;
  pageDescription?: string;
  setPageDescription: (_description: string) => void;
  pageBreadcrumbs?: Array<PageLink>;
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => void;
  pageHeaderNavigation?: Array<PageLink>;
  setPageHeaderNavigation: (_navigation: PageLink[]) => void;
  /** O último nível do breadcrumb ainda carrega (ex.: o nome do fornecedor). */
  pageTitleLoading?: boolean;
  setPageTitleLoading: (_loading: boolean) => void;
}

const PageDataContext = createContext<PageDataContextModel>({
  setPageTitle: (_title: string) => {},
  setPageActions: (_actions: ReactNode) => {},
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => {},
  setPageHeaderNavigation: (_navigation: Array<PageLink>) => {},
  setPageDescription: (_description: string) => {},
  setPageTitleLoading: (_loading: boolean) => {},
});

const PageDataProvider: React.FC = ({children}) => {
  const [pageTitle, setPageTitle] = useState<string>('');
  const [pageActions, setPageActions] = useState<ReactNode>('');
  const [pageDescription, setPageDescription] = useState<string>('');
  const [pageBreadcrumbs, setPageBreadcrumbs] = useState<Array<PageLink>>([]);
  const [pageHeaderNavigation, setPageHeaderNavigation] = useState<
    Array<PageLink>
  >([]);
  const [pageTitleLoading, setPageTitleLoading] = useState<boolean>(false);

  const value: PageDataContextModel = {
    pageTitle,
    setPageTitle,
    pageActions,
    setPageActions,
    pageDescription,
    setPageDescription,
    pageBreadcrumbs,
    setPageBreadcrumbs,
    pageHeaderNavigation,
    setPageHeaderNavigation,
    pageTitleLoading,
    setPageTitleLoading,
  };
  return (
    <PageDataContext.Provider value={value}>
      {children}
    </PageDataContext.Provider>
  );
};

function usePageData() {
  return useContext(PageDataContext);
}

// Os componentes abaixo passam o que a tela declara para o header do app e a
// barra da página. `useLayoutEffect`: a troca acontece antes da pintura, e o
// breadcrumb nunca mostra a página nova com o título da anterior.

type Props = {
  description?: string;
  /**
   * Níveis antes do título no breadcrumb do header. Nível com `title` vazio é
   * um nome que ainda carrega (aparece como placeholder).
   */
  breadcrumbs?: Array<PageLink>;
  /** O título ainda carrega: o header mostra um placeholder no lugar dele. */
  loading?: boolean;
};

const PageTitle: FC<Props> = ({
  children,
  description,
  breadcrumbs,
  loading,
}) => {
  const {
    setPageTitle,
    setPageDescription,
    setPageBreadcrumbs,
    setPageTitleLoading,
  } = usePageData();
  useLayoutEffect(() => {
    if (children) {
      setPageTitle(children.toString());
    }
    return () => {
      setPageTitle('');
    };
  }, [children]);

  useLayoutEffect(() => {
    if (description) {
      setPageDescription(description);
    }
    return () => {
      setPageDescription('');
    };
  }, [description]);

  useLayoutEffect(() => {
    if (breadcrumbs) {
      setPageBreadcrumbs(breadcrumbs);
    }
    return () => {
      setPageBreadcrumbs([]);
    };
  }, [breadcrumbs]);

  useLayoutEffect(() => {
    if (loading) {
      setPageTitleLoading(true);
    }
    return () => {
      setPageTitleLoading(false);
    };
  }, [loading]);

  return <></>;
};

const PageDescription: React.FC = ({children}) => {
  const {setPageDescription} = usePageData();
  useLayoutEffect(() => {
    if (children) {
      setPageDescription(children.toString());
    }
    return () => {
      setPageDescription('');
    };
  }, [children]);
  return <></>;
};

const PageActions: React.FC = ({children}) => {
  const {setPageActions} = usePageData();
  useLayoutEffect(() => {
    if (children) {
      setPageActions(children);
    }
    return () => {
      setPageActions([]);
    };
  }, [children]);
  return <></>;
};

type NavigationProps = {
  headerNavigation?: Array<PageLink>;
};

const HeaderNavigation: React.FC<NavigationProps> = ({headerNavigation}) => {
  const {setPageHeaderNavigation} = usePageData();
  useLayoutEffect(() => {
    if (headerNavigation) {
      setPageHeaderNavigation(headerNavigation);
    }
    return () => {
      setPageHeaderNavigation([]);
    };
  }, [headerNavigation]);
  return <></>;
};

export {
  PageDescription,
  PageTitle,
  PageActions,
  PageDataProvider,
  HeaderNavigation as HeaderNavigations,
  usePageData,
};
